import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Pencil, Trash2, Calendar, Dumbbell, ListChecks,
  Activity, Watch, X,
} from 'lucide-react';
import { workoutsApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate, getMuscleGroupName, cn } from '@/lib/utils';
import { computeWorkoutVolume, computeSetCount, formatVolume } from '@/lib/workout';

// Treenin lukunäkymä (osio 7.2). Read-only; muokkaus omalla lomakkeella
// (/workouts/:id/edit). Poisto säilyy nykyisellä vahvistuslogiikalla.
export function WorkoutDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: workout, isLoading, isError } = useQuery({
    queryKey: ['workout', id],
    queryFn: () => workoutsApi.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => workoutsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      navigate('/workouts');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !workout) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Treeniä ei löytynyt.</p>
        <button onClick={() => navigate('/workouts')} className="btn btn-secondary">
          Takaisin treeneihin
        </button>
      </div>
    );
  }

  const exercises = workout.exercises ?? [];
  const exerciseCount = exercises.length;
  const setCount = computeSetCount(exercises);
  const volume = computeWorkoutVolume(exercises);
  const activities = workout.wearableActivities ?? [];
  const workoutDate = workout.startedAt || workout.createdAt;

  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fade-in">
      {/* Otsikkopalkki */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={cn(
            'p-2 rounded-lg text-gray-500 transition-all touch-manipulation',
            'hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95'
          )}
          aria-label="Takaisin"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
            {workout.name || 'Treeni'}
          </h1>
          <p className="text-sm text-gray-500 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
            {formatDate(workoutDate, 'EEEE d.M.yyyy')}
          </p>
        </div>
      </div>

      {workout.source === 'garmin' && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
          <Watch className="h-3 w-3" aria-hidden="true" />
          Tuotu Garminista
        </span>
      )}

      {/* Yhteenvetokortti */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card p-3 text-center">
          <Dumbbell className="h-5 w-5 mx-auto mb-1 text-primary-500" aria-hidden="true" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">{exerciseCount}</p>
          <p className="text-xs text-gray-500">Liikettä</p>
        </div>
        <div className="card p-3 text-center">
          <ListChecks className="h-5 w-5 mx-auto mb-1 text-primary-500" aria-hidden="true" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">{setCount}</p>
          <p className="text-xs text-gray-500">Sarjaa</p>
        </div>
        <div className="card p-3 text-center">
          <Activity className="h-5 w-5 mx-auto mb-1 text-primary-500" aria-hidden="true" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {volume > 0 ? formatVolume(volume) : '–'}
          </p>
          <p className="text-xs text-gray-500">kg volyymi</p>
        </div>
      </div>

      {/* Liikkeet */}
      {exercises.map((we) => {
        const anyRpe = we.sets.some((s) => s.rpe != null);
        return (
          <div key={we.id} className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {we.exercise?.name || 'Liike'}
              </h2>
              {we.exercise?.muscleGroup && (
                <span className="text-xs text-gray-400">
                  {getMuscleGroupName(we.exercise.muscleGroup)}
                </span>
              )}
            </div>
            {we.sets.length === 0 ? (
              <p className="text-sm text-gray-400">Ei sarjoja</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase tracking-wide text-left">
                      <th className="py-1 pr-3 font-medium">#</th>
                      <th className="py-1 pr-3 font-medium">Paino</th>
                      <th className="py-1 pr-3 font-medium">Toistot</th>
                      {anyRpe && <th className="py-1 font-medium">RPE</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {we.sets.map((s, i) => (
                      <tr key={s.id || i} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1.5 pr-3 text-gray-400">{i + 1}</td>
                        <td className="py-1.5 pr-3 font-medium text-gray-900 dark:text-white">
                          {s.weight != null ? `${Number(s.weight)} kg` : '–'}
                        </td>
                        <td className="py-1.5 pr-3 text-gray-900 dark:text-white">{s.reps ?? '–'}</td>
                        {anyRpe && <td className="py-1.5 text-gray-500">{s.rpe ?? '–'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Garmin-aktiviteetit */}
      {activities.length > 0 && (
        <div className="card p-4 space-y-2">
          <h2 className="font-semibold text-gray-900 dark:text-white">Aktiviteetti</h2>
          {activities.map((a) => (
            <div
              key={a.id}
              className="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-4 gap-y-1"
            >
              <span className="font-medium">{a.activityName || a.activityType}</span>
              {a.durationMinutes ? <span>{a.durationMinutes} min</span> : null}
              {a.distance && Number(a.distance) > 0 ? (
                <span>{(Number(a.distance) / 1000).toFixed(2)} km</span>
              ) : null}
              {a.avgHeartRate ? <span>{a.avgHeartRate} bpm</span> : null}
            </div>
          ))}
        </div>
      )}

      {/* Muistiinpanot */}
      {workout.notes && (
        <div className="card p-4">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">Muistiinpanot</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
            {workout.notes}
          </p>
        </div>
      )}

      {/* Toiminnot */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(`/workouts/${workout.id}/edit`)}
          className="btn btn-primary flex-1 min-h-[48px]"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Muokkaa
        </button>
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="btn btn-secondary min-h-[48px]"
          aria-label="Poista treeni"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Poista
        </button>
      </div>

      {/* Poiston vahvistus */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setConfirmDelete(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Poista treeni"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Poista treeni?</h2>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="p-2 text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Sulje"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Haluatko varmasti poistaa tämän treenin? Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="btn btn-secondary flex-1 min-h-[48px]"
              >
                Peruuta
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn btn-danger flex-1 min-h-[48px]"
              >
                {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
